import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const formatWhen = (d) => {
  if (!d) return '—';
  const dt   = new Date(d);
  const nzt  = new Intl.DateTimeFormat('en-NZ', {
    timeZone: 'Pacific/Auckland',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).formatToParts(dt).reduce((a, p) => ({ ...a, [p.type]: p.value }), {});
  return `${nzt.day}-${nzt.month}-${nzt.year} ${nzt.hour}:${nzt.minute}${nzt.dayPeriod.toLowerCase()}`;
};

const EVENT_ICON  = { ok: '✓', warning: '⚠', error: '✕' };
const EVENT_COLOR = {
  ok:      'var(--green-light)',
  warning: 'var(--gold)',
  error:   '#f87171',
};

// ── Resolution modal ──────────────────────────────────────────────────────────
const ResolutionModal = ({ event, onConfirm, onCancel }) => {
  const [note, setNote]             = useState('');
  const [resolution, setResolution] = useState(null);

  if (!event) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 8, padding: 24, width: 480, maxWidth: '90vw',
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)', marginBottom: 8 }}>
          Resolve Event
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>
          {event.error_message}
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button
            className={`action-btn ${resolution === 'fixed' ? 'action-btn--close' : 'action-btn--ghost'}`}
            onClick={() => setResolution('fixed')}
            style={{ flex: 1, padding: '8px 0' }}
          >
            ✓ Fixed
          </button>
          <button
            className={`action-btn ${resolution === 'ignored' ? 'action-btn--active' : 'action-btn--ghost'}`}
            onClick={() => setResolution('ignored')}
            style={{ flex: 1, padding: '8px 0' }}
          >
            ✕ Ignore
          </button>
        </div>

        <textarea
          placeholder="Add a note (optional) — e.g. loaded manually via GUI, duplicate invoice..."
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={3}
          style={{
            width: '100%', background: 'var(--bg)', border: '1px solid var(--border2)',
            borderRadius: 4, color: 'var(--white)', fontFamily: 'var(--font-body)',
            fontSize: 12, padding: '8px 10px', resize: 'vertical', marginBottom: 16,
            boxSizing: 'border-box',
          }}
        />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="action-btn action-btn--ghost" onClick={onCancel}>Cancel</button>
          <button
            className="action-btn action-btn--close"
            disabled={!resolution}
            onClick={() => onConfirm(event.id, resolution, note)}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Automation Events Page ────────────────────────────────────────────────────
const AutomationEvents = () => {
  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showResolved, setShowResolved] = useState(true);
  const [resolving, setResolving]     = useState(null);
  const [search, setSearch]           = useState('');
  const [errorsOnly, setErrorsOnly]   = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const EVENT_COLUMNS = 'id, job_key, job_name, status, error_message, detail, requires_action, resolved, resolved_by, resolution_note, run_at_utc';

  const load = useCallback(async (resolved = false) => {
    setLoading(true);

    // Unresolved items must never be hidden by a recency limit -- fetch the
    // complete set separately (it's always a small number by definition).
    const unresolvedPromise = supabase
      .from('event_log')
      .select(EVENT_COLUMNS)
      .eq('category', 'automation')
      .eq('requires_action', true)
      .eq('resolved', false)
      .order('run_at_utc', { ascending: false });

    let contextQuery = supabase
      .from('event_log')
      .select(EVENT_COLUMNS)
      .eq('category', 'automation')
      .order('run_at_utc', { ascending: false })
      .limit(200);

    if (!resolved) {
      contextQuery = supabase
        .from('event_log')
        .select(EVENT_COLUMNS)
        .eq('category', 'automation')
        .or(`resolved.eq.false,and(requires_action.eq.false,run_at_utc.gte.${new Date(Date.now() - 86400000).toISOString()})`)
        .order('run_at_utc', { ascending: false })
        .limit(200);
    }

    const [{ data: unresolvedRows }, { data: contextRows }] = await Promise.all([
      unresolvedPromise,
      contextQuery,
    ]);

    const merged = new Map();
    for (const r of (unresolvedRows || [])) merged.set(r.id, r);
    for (const r of (contextRows || [])) merged.set(r.id, r);
    const combined = [...merged.values()].sort(
      (a, b) => new Date(b.run_at_utc) - new Date(a.run_at_utc)
    );

    setRows(combined);
    setLoading(false);
  }, []);

  const handleResolveConfirm = async (id, resolution, note) => {
    await supabase
      .from('event_log')
      .update({
        resolved:        true,
        resolved_by:     resolution,
        resolution_note: note || null,
        resolved_at:     new Date().toISOString(),
      })
      .eq('id', id);
    setRows(prev => prev.map(r =>
      r.id === id ? { ...r, resolved: true, resolved_by: resolution, resolution_note: note || null } : r
    ));
    setResolving(null);
  };

  const handleToggleResolved = () => {
    const next = !showResolved;
    setShowResolved(next);
    load(next);
  };

  useEffect(() => { load(true); }, [load]);

  // ── Client-side filtering ─────────────────────────────────────────────────
  const filteredRows = rows.filter(row => {
    if (errorsOnly && !(row.requires_action && !row.resolved)) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const haystack = [row.job_name, row.job_key, row.error_message]
        .join(' ').toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const unresolvedCount = rows.filter(r => r.requires_action && !r.resolved).length;

  return (
    <main className="app-main">
      <div style={{ padding: '24px 32px' }}>
        <div className="dash-panel">
          <div className="dash-panel__header">
            <span className="dash-panel__title">
              📋 Automation Events
              {unresolvedCount > 0 && (
                <span style={{
                  marginLeft: 8, background: '#f87171', color: '#fff',
                  borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700,
                }}>
                  {unresolvedCount} need attention
                </span>
              )}
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                className="action-btn action-btn--ghost"
                style={{ fontSize: 10, padding: '2px 10px' }}
                onClick={handleToggleResolved}
              >
                {showResolved ? 'Hide Resolved' : 'Show Resolved'}
              </button>
              <button className="dash-refresh-btn" onClick={() => load(showResolved)} disabled={loading}>↻</button>
            </div>
          </div>

          {/* ── Filter bar ──────────────────────────────────────────────── */}
          <div style={{
            display: 'flex', gap: 8, alignItems: 'center',
            padding: '10px 16px', borderBottom: '1px solid var(--border)',
          }}>
            <input
              type="text"
              placeholder="Search app or event… (e.g. Bringans)"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                flex: 1, background: 'var(--bg)', border: '1px solid var(--border2)',
                borderRadius: 4, color: 'var(--white)', fontFamily: 'var(--font-body)',
                fontSize: 12, padding: '5px 10px', outline: 'none',
              }}
            />
            <button
              className={`action-btn ${errorsOnly ? 'action-btn--close' : 'action-btn--ghost'}`}
              style={{ fontSize: 10, padding: '4px 12px', whiteSpace: 'nowrap' }}
              onClick={() => setErrorsOnly(v => !v)}
            >
              {errorsOnly ? '✕ Errors Only' : 'Show Errors Only'}
            </button>
            {(search || errorsOnly) && (
              <button
                className="action-btn action-btn--ghost"
                style={{ fontSize: 10, padding: '4px 10px', color: 'var(--muted)' }}
                onClick={() => { setSearch(''); setErrorsOnly(false); }}
              >
                Clear
              </button>
            )}
          </div>

          {loading ? <div className="dash-loading">Loading…</div> :
           !filteredRows.length ? (
            <div className="dash-empty dash-empty--ok">
              {rows.length ? '✓ No events match filters' : '✓ No recent events'}
            </div>
          ) : (
            <table className="dash-table">
              <thead>
                <tr>
                  <th style={{ width: 24 }}></th>
                  <th>App</th>
                  <th>Event</th>
                  <th>When</th>
                  <th style={{ width: 110 }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.id} className={row.requires_action && !row.resolved ? 'dash-row--action' : ''}>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ color: EVENT_COLOR[row.status] || 'var(--muted)', fontWeight: 700, fontSize: 13 }}>
                        {EVENT_ICON[row.status] || '·'}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{row.job_name}</div>
                      {row.job_key && <div style={{ fontSize: 10, color: 'var(--muted)', opacity: 0.5, fontFamily: 'monospace' }}>{row.job_key}</div>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <div style={{ fontSize: 12, color: 'var(--white)', fontWeight: row.requires_action && !row.resolved ? 600 : 400 }}>
                          {row.error_message}
                        </div>
                        {row.detail?.traceback && (
                          <button
                            onClick={() => setExpandedRow(expandedRow === row.id ? null : row.id)}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              fontSize: 10, color: '#f87171', padding: '0 4px',
                              flexShrink: 0,
                            }}
                          >
                            {expandedRow === row.id ? '▲ hide' : '▼ traceback'}
                          </button>
                        )}
                      </div>
                      {expandedRow === row.id && row.detail?.traceback && (
                        <pre style={{
                          marginTop: 6, padding: '8px 10px',
                          background: 'rgba(248,113,113,0.07)',
                          border: '1px solid rgba(248,113,113,0.2)',
                          borderRadius: 4, fontSize: 10,
                          color: '#fca5a5', whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all', maxHeight: 200,
                          overflowY: 'auto', fontFamily: 'monospace',
                        }}>
                          {row.detail.traceback}
                        </pre>
                      )}
                      {row.resolution_note && (
                        <div style={{ fontSize: 10, color: 'var(--gold)', marginTop: 2, fontStyle: 'italic' }}>
                          Note: {row.resolution_note}
                        </div>
                      )}
                    </td>
                    <td className="muted" style={{ whiteSpace: 'nowrap', fontSize: 11 }}>
                      {formatWhen(row.run_at_utc)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {row.requires_action && !row.resolved ? (
                        <button
                          className="action-btn action-btn--ghost"
                          style={{ fontSize: 10, padding: '2px 10px' }}
                          onClick={() => setResolving(row)}
                        >
                          Resolve ▾
                        </button>
                      ) : row.resolved ? (
                        <span style={{ fontSize: 10, color: row.resolved_by === 'fixed' ? 'var(--green-light)' : 'var(--muted)' }}>
                          {row.resolved_by === 'fixed' ? '✓ Fixed' : '✕ Ignored'}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ResolutionModal
        event={resolving}
        onConfirm={handleResolveConfirm}
        onCancel={() => setResolving(null)}
      />
    </main>
  );
};

export default AutomationEvents;
